"use client"

import type React from "react"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Building2, CheckCircle, ChevronDown, ChevronLeft, Eye, EyeOff, Loader2, Upload } from "lucide-react"

import { azureDb } from "@/lib/azure/compat-client"
import { OTP_CODE_LENGTH } from "@/lib/azure/compat-client"
import { notifyAzureAuthChanged } from "@/lib/azure/auth-events"
import { generateFileName, uploadFile } from "@/lib/azure/upload"
import { DragDropUpload } from "@/components/drag-drop-upload"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { CountryOption, getCountryDisplayName, getFallbackCountries } from "@/lib/countries"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { LegalNotice } from "@/components/legal/legal-notice"

const DEFAULT_EMPLOYER_LOCATION = "Qatar"

export function EmployerSignupPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const otpLength = OTP_CODE_LENGTH
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState("")
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpCode, setOtpCode] = useState("")
  const [pendingUserId, setPendingUserId] = useState<string | null>(null)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [isResendingOtp, setIsResendingOtp] = useState(false)
  const [countries, setCountries] = useState<CountryOption[]>(() => getFallbackCountries())
  const [countriesLoading, setCountriesLoading] = useState(false)

  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    sector: "",
    companySize: "",
    website: "",
    location: DEFAULT_EMPLOYER_LOCATION,
    description: "",
    preferredLanguage: "en",
    agreeTerms: false,
    agreeMarketing: false,
  })
  const [companyLogo, setCompanyLogo] = useState<File | null>(null)

  const { t, i18n } = useTranslation("auth-signup")

  useEffect(() => {
    const authError = searchParams.get("auth_error")
    if (authError) setError(authError)
  }, [searchParams])

  useEffect(() => {
    let isMounted = true
    fetch("https://restcountries.com/v3.1/all?fields=name,translations,cca2")
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("Failed to load countries")))
      .then((data: CountryOption[]) => {
        if (isMounted) setCountries(data.sort((a, b) => a.name.common.localeCompare(b.name.common)))
      })
      .catch(() => undefined)
      .finally(() => {
        if (isMounted) setCountriesLoading(false)
      })
    setCountriesLoading(true)
    return () => { isMounted = false }
  }, [])

  const saveLabProfile = async (userId: string) => {
    if (companyLogo) {
      const fileName = generateFileName(companyLogo.name, `${userId}/`)
      const { error: uploadError } = await uploadFile(
        "company-assets",
        fileName,
        companyLogo,
      )
      if (uploadError) throw new Error(uploadError.message)
    }

    const completion = await fetch("/api/auth/external/complete-onboarding", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        termsAccepted: formData.agreeTerms,
        companyProfile: {
          companyName: formData.companyName,
          phoneNumber: formData.phoneNumber,
          sector: formData.sector,
          companySize: formData.companySize,
          website: formData.website,
          location: formData.location,
          description: formData.description,
          preferredLanguage: formData.preferredLanguage,
        },
      }),
    })
    const result = await completion.json().catch(() => ({}))
    if (!completion.ok) {
      throw new Error(result.error || "Account setup could not be completed.")
    }
    await notifyAzureAuthChanged()
  }

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      setError(t("companyNameRequired") || "Company name is required")
      return false
    }
    if (!formData.email.trim()) {
      setError("Email is required")
      return false
    }
    if (!formData.password) {
      setError("Password is required")
      return false
    }
    if (!formData.phoneNumber.trim()) {
      setError(t("phoneRequired") || "Phone number is required")
      return false
    }
    if (!formData.location) {
      setError(t("locationRequired") || "Location is required")
      return false
    }
    if (!formData.sector) {
      setError(t("sectorRequired") || "Sector is required")
      return false
    }
    if (!formData.companySize) {
      setError(t("companySizeRequired") || "Company size is required")
      return false
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long")
      return false
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match")
      return false
    }
    if (!formData.agreeTerms) {
      setError("You must agree to the terms and conditions")
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const checkResponse = await fetch("/api/check-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          type: "company",
        }),
      })

      if (checkResponse.ok) {
        const data = await checkResponse.json()
        if (data.exists) {
          if (data.conflictType === "job_seeker") {
            setError(t("jobSeekerEmailConflict"))
          } else {
            setError(t("emailAlreadyExists"))
          }
          setIsLoading(false)
          return
        }
      }

      const { data: authData, error: authError } = await azureDb.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            user_type: "company",
            company_name: formData.companyName,
            terms_accepted: formData.agreeTerms,
            company_profile: {
              companyName: formData.companyName,
              phoneNumber: formData.phoneNumber,
              sector: formData.sector,
              companySize: formData.companySize,
              website: formData.website,
              location: formData.location,
              description: formData.description,
              preferredLanguage: formData.preferredLanguage,
            },
          },
        },
      })

      if (authError) {
        setError(authError.message)
        return
      }

      if (!authData.user) {
        setError("Failed to create account")
        return
      }

      setPendingUserId(authData.user.id)
      setShowOtpModal(true)
      toast.success(t("accountCreatedCheckEmail"))
    } catch (signupError) {
      console.error("Signup error:", signupError)
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpVerification = async () => {
    if (!pendingUserId || otpCode.length !== otpLength) {
      setError(t("invalidOtpCode"))
      return
    }

    setIsVerifyingOtp(true)
    setError("")

    try {
      const { data: verifyData, error: verifyError } = await azureDb.auth.verifyOtp({
        email: formData.email,
        token: otpCode,
        type: "email",
      })

      if (verifyError) {
        setError(`Verification failed: ${verifyError.message}`)
        return
      }

      if (!verifyData.user) {
        setError("Verification failed - no user data")
        return
      }

      await saveLabProfile(verifyData.user.id)
      toast.success(t("emailVerifiedSuccess"))
      router.push("/employer/jobs")
      return
    } catch (verificationError) {
      console.error("OTP verification error:", verificationError)
      setError(
        verificationError instanceof Error
          ? verificationError.message
          : "An unexpected error occurred during verification",
      )
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleResendOtp = async () => {
    setIsResendingOtp(true)
    setError("")

    try {
      const { error: resendError } = await azureDb.auth.resend({
        type: "signup",
        email: formData.email,
      })

      if (resendError) {
        if (resendError.message.includes("rate limit")) {
          setError(t("tooManyRequests"))
        } else {
          setError(resendError.message)
        }
        return
      }

      toast.success(t("otpResent"))
    } catch (resendError) {
      console.error("Resend OTP error:", resendError)
      setError("Failed to resend verification code. Please try again.")
    } finally {
      setIsResendingOtp(false)
    }
  }

  const handleLogoUpload = (files: File[]) => {
    if (files.length > 0) {
      setCompanyLogo(files[0])
    }
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6 rtl:space-x-reverse">
          <Link href="/" className="hover:text-blue-600">
            {t("home")}
          </Link>
          <span>{">"}</span>
          <span className="text-gray-900">{t("signUp")}</span>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="flex items-center space-x-3 mb-8 rtl:space-x-reverse">
            <Link href="/">
              <Button variant="ghost" size="icon">
                <ChevronLeft className={`w-5 h-5 ${i18n.language === "ar" ? "rotate-180" : ""}`} />
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">{t("createAccount")}</h1>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Building2 className="w-5 h-5" />
                    <span>{t("employerInformation") || t("companyInformation")}</span>
                  </CardTitle>
                  <CardDescription>{t("createAccountStart")}</CardDescription>
                </CardHeader>
                <CardContent>
                  {error && (
                    <Alert className="mb-6 border-red-200 bg-red-50">
                      <AlertDescription className="text-red-800">{error}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="company-name">
                        {t("companyName")} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="company-name"
                        required
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        placeholder={t("companyNamePlaceholder")}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">
                          {t("email")} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder={t("emailPlaceholder")}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">{t("phoneNumber")} <span className="text-red-500">*</span></Label>
                        <Input
                          id="phone"
                          required
                          value={formData.phoneNumber}
                          onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                          placeholder={t("phonePlaceholder")}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="password">
                          {t("password")} <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            required
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            placeholder={t("passwordPlaceholder")}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500">{t("passwordHint")}</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">
                          {t("confirmPassword")} <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="confirm-password"
                            type={showConfirmPassword ? "text" : "password"}
                            required
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            placeholder={t("confirmPasswordPlaceholder")}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="website">{t("website")}</Label>
                        <Input
                          id="website"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          placeholder={t("websitePlaceholder")}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="location">{t("location")} <span className="text-red-500">*</span></Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal">
                              <span className="truncate">
                                {formData.location
                                  ? getCountryDisplayName(countries.find((country) => country.name.common === formData.location) || { name: { common: formData.location } }, i18n.language)
                                  : countriesLoading ? t("loadingCountries") : t("locationPlaceholder")}
                              </span>
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                            <Command>
                              <CommandInput placeholder={t("searchCountries")} />
                              <CommandList>
                                <CommandEmpty>{t("noCountryFound")}</CommandEmpty>
                                <CommandGroup>
                                  {countries.map((country) => (
                                    <CommandItem key={`company-location-${country.name.common}`} value={country.name.common} onSelect={() => setFormData({ ...formData, location: country.name.common })}>
                                      {getCountryDisplayName(country, i18n.language)}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sector">{t("sector")} <span className="text-red-500">*</span></Label>
                        <Select value={formData.sector} onValueChange={(value) => setFormData({ ...formData, sector: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder={t("selectSector")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="technology">{t("technology")}</SelectItem>
                            <SelectItem value="healthcare">{t("healthcare")}</SelectItem>
                            <SelectItem value="finance">{t("finance")}</SelectItem>
                            <SelectItem value="education">{t("education")}</SelectItem>
                            <SelectItem value="retail">{t("retail")}</SelectItem>
                            <SelectItem value="manufacturing">{t("manufacturing")}</SelectItem>
                            <SelectItem value="consulting">{t("consulting")}</SelectItem>
                            <SelectItem value="other">{t("other")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company-size">{t("companySize")} <span className="text-red-500">*</span></Label>
                        <Select value={formData.companySize} onValueChange={(value) => setFormData({ ...formData, companySize: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder={t("selectCompanySize")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">{t("size-1-10")}</SelectItem>
                            <SelectItem value="11-50">{t("size-11-50")}</SelectItem>
                            <SelectItem value="51-200">{t("size-51-200")}</SelectItem>
                            <SelectItem value="201-500">{t("size-201-500")}</SelectItem>
                            <SelectItem value="501-1000">{t("size-501-1000")}</SelectItem>
                            <SelectItem value="1000+">{t("size-1000+")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="preferred-language">{t("preferredLanguage")}</Label>
                      <Select value={formData.preferredLanguage} onValueChange={(value) => setFormData({ ...formData, preferredLanguage: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder={t("preferredLanguage")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">{t("english")}</SelectItem>
                          <SelectItem value="ar">{t("arabic")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">{t("preferredLanguageDescription")}</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">{t("description")}</Label>
                      <Input
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder={t("descriptionPlaceholder")}
                      />
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Checkbox
                          id="terms"
                          checked={formData.agreeTerms}
                          onCheckedChange={(checked) => setFormData({ ...formData, agreeTerms: checked as boolean })}
                          required
                        />
                        <Label htmlFor="terms" className="text-sm">
                          {t("agreeTerms")}{" "}
                          <Link href="/legal/terms-of-service" target="_blank" className="text-blue-600 hover:underline">
                            (Read Terms of Service)
                          </Link>{" "}
                          <span className="text-red-500">*</span>
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Checkbox
                          id="marketing"
                          checked={formData.agreeMarketing}
                          onCheckedChange={(checked) => setFormData({ ...formData, agreeMarketing: checked as boolean })}
                        />
                        <Label htmlFor="marketing" className="text-sm">
                          {t("agreeMarketing")}{" "}
                          <Link href="/legal/privacy-policy" target="_blank" className="text-blue-600 hover:underline">
                            (Read Privacy Policy)
                          </Link>
                        </Label>
                      </div>

                      <LegalNotice kind="pilot" />
                    </div>

                    <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                      {isLoading ? t("creatingAccount") : t("createAccount")}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>{t("companyLogo")}</CardTitle>
                  <CardDescription>{t("uploadLogo")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <DragDropUpload
                    onFilesUploaded={handleLogoUpload}
                    accept={{ "image/*": [".png", ".jpg", ".jpeg", ".svg"] }}
                    maxFiles={1}
                    maxSize={5 * 1024 * 1024}
                    className="h-48"
                  >
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-700">
                        {companyLogo ? companyLogo.name : t("dropLogoHere")}
                      </p>
                      <p className="text-xs text-gray-500">{t("logoFormats")}</p>
                      <p className="text-xs text-gray-400 mt-1">{t("optionalCanAddLater")}</p>
                    </div>
                  </DragDropUpload>
                </CardContent>
              </Card>

              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">{t("alreadyHaveAccount")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full bg-transparent">
                    <Link href="/auth/employer/login">{t("signIn")}</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t("verifyYourEmail")}</h2>
              <p className="text-gray-600 mb-6">
                {t("otpSentDescription")} <strong>{formData.email}</strong>. {t("otpVerifyInstructions")}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {isVerifyingOtp && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-blue-800 font-medium">{t("verifyingOtp")}</span>
                  </div>
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleOtpVerification() }} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp">{t("otpCodeLabel")} *</Label>
                  <Input
                    id="otp"
                    type="text"
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, otpLength))}
                    placeholder={t("otpCodePlaceholder")}
                    disabled={isVerifyingOtp}
                    maxLength={otpLength}
                    className="text-center text-lg tracking-widest"
                  />
                  <p className="text-xs text-gray-500">{t("otpCodeHint")}</p>
                </div>

                <div className="space-y-3 pt-4">
                  <Button type="submit" className="w-full" disabled={isVerifyingOtp || otpCode.length !== otpLength}>
                    {isVerifyingOtp ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("verifying")}
                      </>
                    ) : (
                      t("verifyAndComplete")
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowOtpModal(false)
                      setOtpCode("")
                      setError("")
                      setPendingUserId(null)
                    }}
                    className="w-full"
                    disabled={isVerifyingOtp}
                  >
                    {t("cancel")}
                  </Button>
                </div>
              </form>

              <div className="text-center mt-4">
                <Button
                  type="button"
                  variant="link"
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={handleResendOtp}
                  disabled={isResendingOtp || isVerifyingOtp}
                >
                  {isResendingOtp ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      {t("resending")}
                    </>
                  ) : (
                    t("resendCode")
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
